/**
 * @fileoverview Logger Configuration and Type Definitions
 * Logging Type Definitions
 * Enhanced type safety for the logging system with generic data support
 *
 * Provides core logging infrastructure including log levels, entry formats,
 * configuration options, and performance timing utilities.
 *
 * @module logger/LoggerConfig
 */

// Base log data types - what can be logged
export type LoggableData =
  | string
  | number
  | boolean
  | null
  | undefined
  | Error
  | Record<string, unknown>
  | unknown[]
  | Date;

// Structured log data for specific use cases
export interface LogData {
  // Performance data
  performance?: {
    operation: string;
    duration: number;
    memory?: number;
    timestamp: string;
  };

  // Error context
  error?: {
    message: string;
    stack?: string;
    code?: string;
    statusCode?: number;
    originalError?: Error;
  };

  // User action data
  userAction?: {
    userId: string;
    action: string;
    target?: string;
    metadata?: Record<string, unknown>;
  };

  // Security event data
  security?: {
    event: string;
    userId?: string;
    ipAddress?: string;
    userAgent?: string;
    threat?: 'low' | 'medium' | 'high' | 'critical';
  };

  // API request/response data
  api?: {
    method: string;
    url: string;
    statusCode?: number;
    duration?: number;
    requestSize?: number;
    responseSize?: number;
  };

  // Database operation data
  database?: {
    operation: 'select' | 'insert' | 'update' | 'delete';
    table: string;
    duration?: number;
    rows?: number;
    query?: string;
  };

  // Validation data
  validation?: {
    field: string;
    value?: unknown;
    rules: string[];
    errors: string[];
  };

  // Generic metadata for any other data
  metadata?: Record<string, LoggableData>;
}

// Enhanced context interface with better typing
export interface EnhancedLogContext {
  component?: string;
  userId?: string;
  sessionId?: string;
  action?: string;
  error?: Error;
  timestamp?: string;

  // Structured data
  data?: LogData;

  // Raw metadata for backward compatibility
  metadata?: Record<string, LoggableData>;
}

// Specific context types for different logging scenarios
export interface ErrorLogContext extends EnhancedLogContext {
  error: Error;
  data?: LogData & {
    error: LogData['error'];
  };
}

export interface PerformanceLogContext extends EnhancedLogContext {
  data: LogData & {
    performance: LogData['performance'];
  };
}

export interface UserActionLogContext extends EnhancedLogContext {
  userId: string;
  data?: LogData & {
    userAction: LogData['userAction'];
  };
}

export interface SecurityLogContext extends EnhancedLogContext {
  data: LogData & {
    security: LogData['security'];
  };
}

// Generic logger method signatures
export interface Logger {
  error(message: string, context?: EnhancedLogContext): void;
  error(message: string, data?: LoggableData): void;
  error(
    message: string,
    context?: EnhancedLogContext,
    data?: LoggableData,
  ): void;

  warn(message: string, context?: EnhancedLogContext): void;
  warn(message: string, data?: LoggableData): void;
  warn(
    message: string,
    context?: EnhancedLogContext,
    data?: LoggableData,
  ): void;

  info(message: string, context?: EnhancedLogContext): void;
  info(message: string, data?: LoggableData): void;
  info(
    message: string,
    context?: EnhancedLogContext,
    data?: LoggableData,
  ): void;

  debug(message: string, context?: EnhancedLogContext): void;
  debug(message: string, data?: LoggableData): void;
  debug(
    message: string,
    context?: EnhancedLogContext,
    data?: LoggableData,
  ): void;

  trace(message: string, context?: EnhancedLogContext): void;
  trace(message: string, data?: LoggableData): void;
  trace(
    message: string,
    context?: EnhancedLogContext,
    data?: LoggableData,
  ): void;
}

// Specialized logger methods
export interface SpecializedLogger {
  performance(
    operation: string,
    duration: number,
    context?: EnhancedLogContext,
  ): void;
  userAction(action: string, context?: UserActionLogContext): void;
  security(event: string, context?: SecurityLogContext): void;
  apiRequest(method: string, url: string, context?: EnhancedLogContext): void;
  apiResponse(
    statusCode: number,
    duration: number,
    context?: EnhancedLogContext,
  ): void;
}

// Component-specific logger interface
export interface ComponentLogger {
  error(message: string, data?: LoggableData): void;
  warn(message: string, data?: LoggableData): void;
  info(message: string, data?: LoggableData): void;
  debug(message: string, data?: LoggableData): void;
  trace(message: string, data?: LoggableData): void;
  userAction(action: string, data?: LoggableData): void;
  performance(operation: string, duration: number): void;
}

// Type guards for log data
export const isLogData = (data: unknown): data is LogData => {
  return typeof data === 'object' && data !== null && !Array.isArray(data);
};

export const isLoggableData = (data: unknown): data is LoggableData => {
  if (data === null || data === undefined) return true;
  if (
    typeof data === 'string' ||
    typeof data === 'number' ||
    typeof data === 'boolean'
  )
    return true;
  if (data instanceof Error || data instanceof Date) return true;
  if (Array.isArray(data)) return true;
  if (typeof data === 'object') return true;
  return false;
};

/**
 * Enumeration of logging severity levels
 *
 * @enum {number}
 * @readonly
 */
export enum LogLevel {
  /** Debug messages for detailed troubleshooting */
  DEBUG = 0,
  /** Informational messages about normal operation */
  INFO = 1,
  /** Warning messages for potentially problematic situations */
  WARN = 2,
  /** Error messages for failures and exceptions */
  ERROR = 3,
}

/**
 * Structure of a single log entry
 *
 * @interface LogEntry
 */
export interface LogEntry {
  /** ISO 8601 timestamp when the log was created */
  timestamp: string;
  /** Severity level of the log entry */
  level: LogLevel;
  /** Human-readable log message */
  message: string;
  /** Optional context identifier (e.g., component or module name) */
  context?: string;
  /** Optional structured metadata attached to the log */
  metadata?: Record<string, LoggableData>;
  /** Optional user identifier if available */
  userId?: string;
  /** Session identifier for tracking related logs */
  sessionId?: string;
  /** Optional error object for ERROR level logs */
  error?: Error;
  /** Optional component name */
  component?: string;
  /** Optional additional data */
  data?: Record<string, unknown>;
}

/**
 * Logger configuration options
 *
 * @interface LoggerConfig
 */
export interface LoggerConfig {
  /** Minimum log level to output (logs below this level are ignored) */
  level: LogLevel;
  /** Whether to output logs to browser console */
  enableConsole: boolean;
  /** Whether to include timestamps in log output */
  enableTimestamps: boolean;
  /** Whether to include context in log output */
  enableContext: boolean;
  /** Maximum number of log entries to retain in memory */
  maxEntries: number;
  /** Category-based filtering - enables/disables specific log categories */
  categories: {
    [key: string]: boolean;
  };
}

/**
 * Valid log category types
 * Corresponds to the categories defined in DEFAULT_CONFIG
 *
 * @type LogCategory
 */
export type LogCategory =
  | 'auth'
  | 'navigation'
  | 'field_operations'
  | 'storage'
  | 'api'
  | 'performance'
  | 'ui'
  | 'security'
  | 'error';

/**
 * Performance timer data structure
 *
 * Used for measuring operation duration using performance.now()
 *
 * @interface PerformanceTimer
 */
export interface PerformanceTimer {
  /** Human-readable label for the timer */
  label: string;
  /** High-resolution start time from performance.now() */
  startTime: number;
  /** Optional context where the timer was started */
  context?: string;
  /** Optional metadata associated with the timer */
  metadata?: Record<string, LoggableData>;
}

/**
 * Mapping of log levels to human-readable names
 *
 * @constant
 */
export const LOG_LEVEL_NAMES: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: 'DEBUG',
  [LogLevel.INFO]: 'INFO',
  [LogLevel.WARN]: 'WARN',
  [LogLevel.ERROR]: 'ERROR',
};

/**
 * Mapping of log levels to console output colors
 *
 * @constant
 */
export const LOG_LEVEL_COLORS: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: '#6B7280', // Gray
  [LogLevel.INFO]: '#3B82F6', // Blue
  [LogLevel.WARN]: '#F59E0B', // Amber
  [LogLevel.ERROR]: '#EF4444', // Red
};

export interface LogBatch {
  userId: string;
  component: string;
  entries: LogEntry[];
  startTime: Date;
}
